import { arrayToMobxMap } from '@doltech/utils/lib/object';
import { types } from 'mobx-state-tree';
import { useStore } from 'mobx-store-provider';

import { ExerciseGapFillWithHintDoTestStore } from '../../plugins/ExerciseGapFillWithHint/store/exercise-gap-fill-with-hint-do-test.store';
import { ExerciseMultipleChoiceDoTestStore } from '../../plugins/ExerciseMultipleChoice/store/exercise-multiple-choice-do-test.store';
import { ExerciseQuizDoTestStore } from '../../plugins/ExerciseQuiz/store/exercise-quiz-do-test.store';
import { ExerciseShortAnswerDoTestStore } from '../../plugins/ExerciseShortAnswer/store/exercise-short-answer-do-test.store';
import { ExerciseGapFillDoTestStore } from '../../plugins/GapFill/store/exercise-gap-fill-do-test.store';
import { ExerciseProgress } from './types';

const reduceUserAnswer = (userAnswers) => {
  return userAnswers.reduce(
    (map, { questionId, answer }) => ({
      ...map,
      [questionId]: {
        id: questionId,
        userAnswer: answer,
      },
    }),
    {}
  );
};

export const DoExerciseProgressStore = types
  .model('DoExerciseProgressStore', {
    multipleChoiceStores: types.map(ExerciseMultipleChoiceDoTestStore),
    gapFillStores: types.map(ExerciseGapFillDoTestStore),
    gapFillWithHintStores: types.map(ExerciseGapFillWithHintDoTestStore),
    shortAnswerStores: types.map(ExerciseShortAnswerDoTestStore),
    quizStores: types.map(ExerciseQuizDoTestStore),
  })
  .actions((self) => {
    return {
      init(exerciseProgresses: ExerciseProgress[]) {
        exerciseProgresses.forEach((exercise) => {
          switch (exercise.type) {
            case 'MULTIPLE_CHOICE': {
              const multipleChoiceStore = ExerciseMultipleChoiceDoTestStore.create({
                id: exercise.exerciseId,
                isViewResult: exercise.isCompleted,
              });
              multipleChoiceStore.setUserAnswers(
                exercise.userAnswers.reduce(
                  (map, { questionId, answerIds }) => ({
                    ...map,
                    [questionId]: {
                      questionId,
                      answers: answerIds,
                    },
                  }),
                  {}
                )
              );
              self.multipleChoiceStores.put(multipleChoiceStore);
              break;
            }
            case 'GAP_FILL': {
              const gapFillStore = ExerciseGapFillDoTestStore.create({
                id: exercise.exerciseId,
                isViewResult: exercise.isCompleted,
              });

              gapFillStore.setUserAnswers(reduceUserAnswer(exercise.userAnswers));

              self.gapFillStores.put(gapFillStore);
              break;
            }
            case 'SHORT_ANSWER': {
              const shortAnswer = ExerciseShortAnswerDoTestStore.create({
                id: exercise.exerciseId,
                isViewResult: exercise.isCompleted,
              });

              shortAnswer.setUserAnswers(reduceUserAnswer(exercise.userAnswers));

              self.shortAnswerStores.put(shortAnswer);
              break;
            }
            case 'SUMMARY_COMPLETION_WITH_HINT': {
              let gapFillWithHintStore = self.gapFillWithHintStores.get(exercise.exerciseId);
              const userAnswers = arrayToMobxMap(
                exercise.userAnswers,
                'questionId',
                ({ questionId, answerId }) => ({ id: questionId, userAnswer: answerId })
              );
              if (!gapFillWithHintStore) {
                gapFillWithHintStore = ExerciseGapFillWithHintDoTestStore.create({
                  id: exercise.exerciseId,
                  isViewResult: exercise.isCompleted,
                  userAnswers,
                });
              } else if (exercise.isCompleted) {
                gapFillWithHintStore.viewResult();
              }
              if (exercise.userAnswers instanceof Array) {
                gapFillWithHintStore.initUserAnswers(userAnswers);
              }

              self.gapFillWithHintStores.put(gapFillWithHintStore);
              break;
            }
            case 'QUIZ': {
              const data: any = exercise;
              const quizStore = ExerciseQuizDoTestStore.create({
                id: data.exerciseId
              });
              if (data.element) {
                const questionCount = data.element?.children?.[0]?.children?.length;
                quizStore.setQuizCount(questionCount);
                quizStore.setQuizIds(data.element?.children?.[0]?.children?.map(it => it.id));
              }
              if (data.statistics) {
                quizStore.setStatistics(
                  arrayToMobxMap(data.statistics, 'questionId', (value) => ({
                    ...value,
                    answers: arrayToMobxMap(value.answers, 'answerId'),
                  }))
                );
              }
              if (data.userAnswers instanceof Array) {
                quizStore.setUserAnswers(
                  arrayToMobxMap(data.userAnswers, 'questionId', (value) => ({
                    ...value,
                    submitted: true,
                  }))
                );
                quizStore.setQuizIndex(Math.max(data.userAnswers.length - 1, 0));
              }

              const currentQuizStore = self.quizStores.get(exercise.exerciseId);
              if (currentQuizStore && currentQuizStore.quizCount > 0) {
                quizStore.setQuizIndex(
                  Math.max(
                    Math.min(currentQuizStore.userAnswers.size - 1, currentQuizStore.quizCount - 1),
                    0
                  )
                );
              }
              self.quizStores.put(quizStore);
              break;
            }
            default:
              break;
          }
        });
      },
    };
  })
  .views((self) => {
    return {};
  });

export const useDoExerciseProgressStore = () => useStore(DoExerciseProgressStore);
